import {
  type Folder,
  type FolderId,
  type FolderSection,
  folderNameProblem,
  MAX_FOLDER_DEPTH,
  MAX_FOLDER_NAME_CHARS,
  normalizeFolderName,
} from "@esi/shared";
import { Check, ChevronRight, FolderClosed, FolderOpen, FolderPlus, Search } from "lucide-react";
import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { InlineError } from "@/components/Notice";
import { TreeView } from "@/components/tree/Tree";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { folderErrorText, useFolders, useFolderWrite } from "./folders.ts";
import {
  type FolderNode,
  folderNodeId,
  folderPath,
  knowledgeTree,
  type SectionNode,
  searchTree,
  sectionId,
  type TreeRow,
  visibleRows,
} from "./knowledge-tree.ts";

type PickerRow = TreeRow & { id: string; node: SectionNode | FolderNode };

/** A folder's path as names joined by "›", from the section's name (design §6). */
export function pathText(sectionName: string, names: readonly string[]): string {
  return [sectionName, ...names].join(" › ");
}

/**
 * One section's folders as a tree to choose a target from (design §7): its root, then every
 * folder, expanded in place, with a search over names and "New folder". A target that cannot
 * be chosen (too deep, the moved folder itself) stays listed, disabled with its reason.
 */
export function FolderPicker(props: {
  projectId: string;
  folders: readonly Folder[];
  section: FolderSection;
  /** The chosen folder; null is the section root. */
  value: FolderId | null;
  onChange: (folderId: FolderId | null) => void;
  /** Why a target cannot be chosen, or null (root: `node` null). */
  disabledReason?: (node: FolderNode | null) => string | null;
  /** Offer "New folder" under the chosen target (`folder.manage`). */
  canCreate?: boolean;
  /** The visible label of the picker (its tree's accessible name). */
  label: string;
}) {
  const { t } = useTranslation();
  const id = useId();
  const tree = useMemo(() => knowledgeTree({ folders: props.folders }), [props.folders]);
  const root = sectionId(props.section);
  const selected = props.value ? folderNodeId(props.value) : root;
  // The chosen folder starts revealed: its ancestors are open.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => {
    const node = tree.byId.get(selected);
    return new Set([root, ...(node ? folderPath(tree, node).map((f) => f.id) : [])]);
  });
  const [focused, setFocused] = useState<string | null>(selected);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const write = useFolderWrite(props.projectId);
  const search = useMemo(() => searchTree(tree, query), [tree, query]);
  // Toggles made while searching are the search's own, over what it opened (as in the tree).
  const [searchOpen, setSearchOpen] = useState<ReadonlyMap<string, boolean>>(new Map());
  const open = useMemo(() => {
    if (!search) {
      return expanded;
    }
    const all = new Set([...expanded, ...search.expanded, root]);
    for (const [nodeId, on] of searchOpen) {
      on ? all.add(nodeId) : all.delete(nodeId);
    }
    return all;
  }, [expanded, search, searchOpen, root]);
  const rows = useMemo(
    () =>
      visibleRows(tree, open, null, search && { ...search, expanded: new Set() }, [
        props.section,
      ]).map((r) => ({ ...r, id: r.node.id })) as PickerRow[],
    [tree, open, search, props.section],
  );
  const shown = rows;
  const sectionName = t(`knowledgePanel.section.${props.section}`);
  const reasonOf = (node: SectionNode | FolderNode) =>
    props.disabledReason?.(node.kind === "folder" ? node : null) ?? null;
  const choose = (node: SectionNode | FolderNode) => {
    if (!reasonOf(node)) {
      props.onChange(node.kind === "folder" ? node.folder.id : null);
    }
  };
  const target = tree.byId.get(selected);
  const targetDepth = target?.kind === "folder" ? target.depth : 0;
  const newIn = t("folders.newIn", {
    path: pathText(
      sectionName,
      target?.kind === "folder" ? [...folderPath(tree, target), target].map((f) => f.name) : [],
    ),
  });
  const problem = folderNameProblem(normalizeFolderName(name));
  const create = () => {
    if (problem || write.isPending) {
      return;
    }
    write.mutate(
      { op: "create", section: props.section, parentId: props.value, name },
      {
        onSuccess: (result) => {
          if (result.op === "create") {
            setExpanded((was) => new Set([...was, selected]));
            props.onChange(result.folder.id);
            setFocused(folderNodeId(result.folder.id));
          }
          setCreating(false);
          setName("");
        },
      },
    );
  };
  const toggle = (rowId: string, on: boolean) =>
    search
      ? setSearchOpen((was) => new Map(was).set(rowId, on))
      : setExpanded((was) => {
          const next = new Set(was);
          on ? next.add(rowId) : next.delete(rowId);
          return next;
        });

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-fg-muted"
          strokeWidth={1.75}
        />
        <Input
          type="search"
          value={query}
          aria-label={t("folders.picker.search")}
          placeholder={t("folders.picker.search")}
          onChange={(e) => {
            setQuery(e.currentTarget.value);
            setSearchOpen(new Map());
          }}
          onKeyDown={(e) => {
            // The picker sits inside other forms (upload, save, publish): Enter in its search
            // never submits them.
            if (e.key === "Enter") {
              e.preventDefault();
            }
            if (e.key === "Escape" && query) {
              // Clears the field without closing the dialog around it.
              e.preventDefault();
              e.stopPropagation();
              setQuery("");
              setSearchOpen(new Map());
            }
          }}
          className="h-8 pl-8 text-sm"
        />
      </div>
      <div className="rounded-lg border border-line bg-surface-raised p-1">
        {shown.length === 0 ? (
          <p className="px-2 py-3 text-sm text-fg-secondary">{t("folders.picker.noMatch")}</p>
        ) : (
          <TreeView
            label={props.label}
            // The tree is the scroll owner, so a long, virtualised list scrolls to its focus; a
            // short viewport (a phone) gets a shorter list, so the dialog's actions stay in view.
            className="max-h-[min(16rem,40dvh)]"
            rows={shown}
            focusedId={focused}
            onFocusChange={setFocused}
            onToggle={(row, on) => toggle(row.id, on)}
            onActivate={(row) => choose(row.node)}
            onExpandSiblings={(row) => {
              const siblings = shown.filter(
                (r) => r.node.parentId === row.node.parentId && r.expanded !== undefined,
              );
              for (const r of siblings) {
                toggle(r.id, true);
              }
            }}
            nameOf={(row) => (row.node.kind === "section" ? sectionName : row.node.name)}
            itemProps={(row) => ({
              "aria-selected": row.id === selected,
              ...(reasonOf(row.node) && { "aria-describedby": `${id}-${row.id}-why` }),
            })}
            estimateSize={() => 32}
            rowClassName={(row) =>
              cn(
                "rounded-md",
                row.id === selected ? "bg-surface-selected" : "hover:bg-surface-hover",
                reasonOf(row.node) && "cursor-not-allowed",
              )
            }
            renderRow={(row) => {
              const reason = reasonOf(row.node);
              const Icon = row.expanded ? FolderOpen : FolderClosed;
              return (
                <span
                  className="flex min-h-8 items-center gap-1.5 py-1 pr-2 text-sm"
                  style={{ paddingLeft: `${4 + Math.min(row.level - 1, 3) * 16}px` }}
                >
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-hidden="true"
                    onClick={() => toggle(row.id, !row.expanded)}
                    className={cn(
                      "grid size-5 shrink-0 place-items-center rounded-sm text-fg-muted hover:text-fg",
                      row.node.kind === "folder" && row.node.children.length === 0 && "invisible",
                    )}
                  >
                    <ChevronRight
                      className={cn(
                        "size-3.5 transition-transform duration-(--duration-fast)",
                        row.expanded && "rotate-90",
                      )}
                      strokeWidth={1.75}
                    />
                  </button>
                  <Icon
                    aria-hidden="true"
                    className="size-4 shrink-0 text-fg-muted"
                    strokeWidth={1.5}
                  />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span
                      // Names wrap in full: on touch the path's tooltip is out of reach.
                      className={cn("wrap-anywhere", reason ? "text-fg-muted" : "text-fg")}
                      title={
                        row.node.kind === "folder"
                          ? pathText(
                              sectionName,
                              [...folderPath(tree, row.node), row.node].map((f) => f.name),
                            )
                          : undefined
                      }
                    >
                      {row.node.kind === "section"
                        ? t("folders.picker.root", { section: sectionName })
                        : row.node.name}
                    </span>
                    {reason ? (
                      <span id={`${id}-${row.id}-why`} className="text-xs text-fg-muted">
                        {reason}
                      </span>
                    ) : null}
                  </span>
                  {row.id === selected ? (
                    <Check
                      aria-hidden="true"
                      className="size-4 shrink-0 text-fg"
                      strokeWidth={1.75}
                    />
                  ) : null}
                </span>
              );
            }}
          />
        )}
      </div>
      {props.canCreate ? (
        creating ? (
          // Not a form: the picker sits inside other forms (upload, save).
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <Input
                autoFocus
                value={name}
                maxLength={MAX_FOLDER_NAME_CHARS * 4}
                aria-label={t("folders.nameLabel")}
                aria-describedby={`${id}-visible`}
                placeholder={t("folders.nameLabel")}
                onChange={(e) => setName(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    create();
                  }
                }}
                className="h-8 text-sm"
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={problem !== null || write.isPending}
                onClick={create}
              >
                {t("folders.create")}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setCreating(false);
                  write.reset();
                }}
              >
                {t("files.cancel")}
              </Button>
            </div>
            <p id={`${id}-visible`} className="text-xs text-fg-muted">
              {t("folders.visibleNote")}
            </p>
            {problem && problem !== "empty" ? (
              <InlineError>
                {t(`folders.nameProblem.${problem}`, { max: MAX_FOLDER_NAME_CHARS })}
              </InlineError>
            ) : null}
            {write.error ? <InlineError>{folderErrorText(write.error)}</InlineError> : null}
          </div>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="-ml-2 max-w-full self-start"
            disabled={targetDepth >= MAX_FOLDER_DEPTH}
            title={newIn}
            onClick={() => setCreating(true)}
          >
            <FolderPlus aria-hidden="true" strokeWidth={1.75} />
            <span className="truncate">{newIn}</span>
          </Button>
        )
      ) : null}
    </div>
  );
}

/**
 * Where a write lands (upload, save as file, save to wiki, publish): the folder's path, and
 * "Change" opens the picker in place. `fixed` shows the folder only (a page edit keeps its own).
 */
export function FolderField(props: {
  projectId: string;
  section: FolderSection;
  value: FolderId | null;
  onChange: (folderId: FolderId | null) => void;
  canCreate?: boolean;
  fixed?: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  const folders = useFolders(props.projectId);
  const [picking, setPicking] = useState(false);
  const list = folders.data?.folders;
  const tree = useMemo(() => knowledgeTree({ folders: list ?? [] }), [list]);
  const sectionName = t(`knowledgePanel.section.${props.section}`);
  const node = props.value ? tree.byId.get(folderNodeId(props.value)) : undefined;
  // A chosen folder deleted meanwhile (the list was read again): the server would refuse it,
  // so the choice falls back to the root, which is what the field then shows.
  const missing = list !== undefined && props.value !== null && node === undefined;
  const { onChange } = props;
  useEffect(() => {
    if (missing) {
      onChange(null);
    }
  }, [missing, onChange]);
  const path = pathText(
    sectionName,
    node?.kind === "folder" ? [...folderPath(tree, node), node].map((f) => f.name) : [],
  );
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span id={`${id}-label`} className="text-sm font-medium text-fg">
          {t("folders.field")}
        </span>
        <span className="inline-flex max-w-full min-w-0 items-center gap-1.5 text-sm text-fg-secondary">
          <FolderClosed
            aria-hidden="true"
            className="size-4 shrink-0 text-fg-muted"
            strokeWidth={1.5}
          />
          <span className="wrap-anywhere">{path}</span>
        </span>
        {props.fixed || !list ? null : (
          <Button
            type="button"
            size="xs"
            variant="ghost"
            aria-expanded={picking}
            onClick={() => setPicking((p) => !p)}
          >
            {picking ? t("folders.picker.done") : t("folders.picker.change")}
          </Button>
        )}
      </div>
      {picking && list ? (
        <FolderPicker
          projectId={props.projectId}
          folders={list}
          section={props.section}
          value={node ? props.value : null}
          onChange={props.onChange}
          canCreate={props.canCreate}
          label={t("folders.field")}
        />
      ) : null}
    </div>
  );
}
